import produce from 'immer';
import cloneDeep from 'lodash/cloneDeep';

import {SingleOrMultiSegment} from 'common/widgets/query-builder/types';
import {
  isEventBasedType,
  isGroupedEntry,
  isUserBasedType,
  QueryEntry,
  QueryEventEntry,
  QuerySegmentEntry,
  SelectorTypeDefinitions,
} from 'common/widgets/query-entry-section/types';
import {ProjectFeatureFlag, ResourceType} from 'common/report/constants';
import {
  getSegmentType,
  isEventSegment,
  isMultiGroupBy,
  isNonEmptySegmentSelection,
  isUserSegment,
  Segment,
} from 'common/widgets/query-block/segment-menu/types';
import {ButtonTheme} from 'common/components/button/constants';
import {currentProjectStore} from 'common/report/stores/project-details';
import {getReportName} from 'common/report/util';

/**
 * Sanitizes exclusionSteps by fixing out of range step selections
 */
export function sanitizeExclusionSteps(exclusionSteps: QueryEventEntry[], events: QueryEntry[]): QueryEventEntry[] {
  return exclusionSteps.map((step) => {
    if (`to` in step.excludeRange && step.excludeRange.to > events.length) {
      step.excludeAll = true;
      step.excludeRange = {};
    }

    return step;
  });
}

// The format used by mp-query-builder and mp-query-entry-section are different. So it's necessary to map
// between these two formats.
export function transformToQuerySegmentEntries(groupBy: Segment[][]): QuerySegmentEntry[] {
  return cloneDeep(groupBy).map((groupByItem: Segment[]) => {
    let customBucket;
    const segment = groupByItem[0];
    if (isEventSegment(segment) || isUserSegment(segment)) {
      const segmentation = Object.values(segment)[0];
      if (segmentation.customBucket) {
        // The QuerySegmentEntry format has the `customBucket` as a top-level field. So we remove `customBucket`
        // from the segment and move it to be a top-level field.
        customBucket = segmentation.customBucket;
        delete segmentation.customBucket;
      }
    }
    return {
      event: groupByItem,
      filters: [],
      type: SelectorTypeDefinitions.Segment,
      ...(customBucket ? {customBucket} : {}),
    };
  });
}

export function transformFromQuerySegmentEntries(entries: QuerySegmentEntry[]): Segment[][] {
  return entries.map((entry: QuerySegmentEntry) => {
    return produce(entry.event, (segment) => {
      if (entry.customBucket) {
        Object.values(segment[0])[0].customBucket = entry.customBucket;
      }
    });
  });
}

/**
 * Sanitizes group-by entries when a cohort is selected
 */
export function sanitizeGroupBys(events: QueryEntry[], segments: SingleOrMultiSegment) {
  const eventsOnlyEntries = events.every((entry) => isEventBasedType(entry.type) || isGroupedEntry(entry));
  const peopleOnlyEntries = events.length > 0 && events.every((entry) => isUserBasedType(entry));

  let updatedSegments;
  if (isMultiGroupBy(segments)) {
    updatedSegments = segments.filter((segment) => isNonEmptySegmentSelection(segment));
  } else {
    updatedSegments = [...segments] as Segment[];
  }

  if (peopleOnlyEntries) {
    const allowedTypes = [ResourceType.User, ResourceType.Cohort];
    updatedSegments = updatedSegments.filter((segment) => allowedTypes.includes(getSegmentType(segment)));
  } else if (!eventsOnlyEntries) {
    updatedSegments = updatedSegments.filter((segment) => getSegmentType(segment) === ResourceType.Cohort);
  }
  return updatedSegments;
}

/** TODO(pablo): drop this method once internal testing is completed */
export const qbStyleAttrs = () => ({
  selectorTheme: hasNewQBUI() ? ButtonTheme.Tertiary : ButtonTheme.Select,
  selectorType: `button`,
  buttonTheme: ButtonTheme.Select,
});

/**
 * Enable new QB only for funnels
 *
 * @todo [pablo] figure out a way to cache this so we stop content flashing/re-shifting as data loads
 */
export function hasNewQBUI() {
  return (
    [`funnels`, `insights`, `retention`, `flows`].includes(getReportName()) &&
    currentProjectStore.value?.hasFeatureFlag(ProjectFeatureFlag.QBSidebar)
  );
}
